import { useEffect, useMemo, useState } from 'react';
import { FaAngleDown, FaAngleUp } from 'react-icons/fa';
import { useQuery } from '@apollo/client';

import LoadingSpinner from '../LoadingSpinner';
import ErrorText from '../ErrorText';
import DraggableAttribute from './DraggableAttribute';
import FileStatusBadge from '../FileStatusBadge';
import UploadedFilesModal, { UploadedFile } from './UploadedFilesModal';
import { FILES_QUERY } from '../../queries';
import { TYPES } from '../../reducers/builderReducer';
import { isIdFile } from '../../helpers/fileKinds';

const { MY_DATA_ATTRIBUTE } = TYPES;

// Statuses that are still "in motion" and warrant aggressive polling so the
// user sees progress updates in (near) real time. Mirrors the polling logic
// in routes/mywork/UploadedFiles.tsx.
const IDLE_STATUSES = new Set([
  'completed',
  'processing-failed',
  'matching-failed',
  'uploading-failed',
  'failed',
]);

const TOP_N = 5;

interface UploadedFilesListProps {
  className?: string;
  /** Initial open state of the dropdown. Defaults to true so the user sees
   *  their files immediately, matching the screenshot reference. */
  initialOpen?: boolean;
  /**
   * Called when the user clicks "Add to Include" / "Add to Exclude" inside
   * the modal. The parent wires this through the builder reducer.
   */
  onAddFile: (file: UploadedFile, zoneType: 'include' | 'exclude') => void;
  /**
   * Predicate to determine whether a file can still be added to the given
   * zone (i.e. it isn't already present in include/exclude). Lives in the
   * parent because it depends on builderState.
   */
  canAddToZone: (
    fileId: string,
    zoneType: 'include' | 'exclude'
  ) => boolean;
}

/**
 * Sidebar list of the user's uploaded files. Shows the 5 most-recently
 * uploaded files inline (draggable into the builder, same as before) and a
 * "View all files" affordance that opens a searchable modal with the full
 * list and live progress tracking.
 */
export default function UploadedFilesList({
  className,
  initialOpen = true,
  onAddFile,
  canAddToZone,
}: UploadedFilesListProps) {
  const [open, setOpen] = useState(initialOpen);
  const [modalOpen, setModalOpen] = useState(false);

  const { loading, error, data, startPolling, stopPolling } = useQuery(
    FILES_QUERY,
    {
      fetchPolicy: 'cache-and-network',
    }
  );

  // ATAD-136: ZIP code files have their own sidebar section, and so do
  // ATAD-146 model files. Filtering here keeps them out of the top-N list,
  // the polling check and the modal.
  const files: UploadedFile[] = useMemo(
    () => (data?.files ?? []).filter(isIdFile),
    [data?.files]
  );

  // Sort by createdAt desc and slice to the top N for the inline dropdown.
  // The modal sees the full unsorted list and applies its own default sort.
  const topFiles = useMemo(() => {
    return [...files]
      .sort((a, b) => {
        const aTime = new Date(a.createdAt).getTime();
        const bTime = new Date(b.createdAt).getTime();
        return bTime - aTime;
      })
      .slice(0, TOP_N);
  }, [files]);

  // Poll while any file is still in-flight so the badges/counts in both the
  // dropdown and the modal animate forward without a full page refresh.
  const allFilesIdle = files.every((file) => IDLE_STATUSES.has(file.status));

  useEffect(
    function managePollForStatusUpdates() {
      if (files.length > 0 && !allFilesIdle) {
        startPolling(1000);
      } else {
        stopPolling();
      }
      return () => stopPolling();
    },
    [files.length, allFilesIdle, startPolling, stopPolling]
  );

  const hasMore = files.length > TOP_N;

  return (
    <div className={`w-full ${className ?? ''}`}>
      <div
        onClick={() => setOpen((prev) => !prev)}
        className="flex flex-row items-center hover:bg-gray-200 cursor-pointer p-1 -ml-2 rounded-lg"
      >
        <div>{open ? <FaAngleUp /> : <FaAngleDown />}</div>
        <div className="pl-1">Uploaded Files</div>
      </div>

      <div
        className={`py-2 pl-5 flex flex-col w-full overflow-auto ${
          open ? 'visible' : 'hidden'
        }`}
      >
        {loading && files.length === 0 && (
          <LoadingSpinner message="Getting your uploaded files..." />
        )}
        {!loading && error && <ErrorText message={error.message} />}
        {(!loading || files.length > 0) && !error && (
          <>
            <ul>
              {topFiles.map((file) => {
                const isDraggable = file.crawledAt != null;
                const isCompleted = file.status === 'completed';
                return (
                  <li key={file.id} className="py-0.5">
                    <DraggableAttribute
                      id={file.id}
                      name={file.name}
                      resourceType="file"
                      type={MY_DATA_ATTRIBUTE}
                      disabled={!isDraggable}
                    />
                    {/* For in-progress / failed files, surface a compact
                        status indicator beneath the (disabled) name so the
                        user knows why they can't drag it yet. */}
                    {!isCompleted && (
                      <div className="pl-1 mt-0.5">
                        <FileStatusBadge
                          status={file.status}
                          imported={!!file.dataSource}
                        />
                      </div>
                    )}
                  </li>
                );
              })}
              {topFiles.length === 0 && (
                <li>
                  <p className="text-sm text-gray-500">No uploaded files</p>
                </li>
              )}
            </ul>

            {/* "View all" affordance: always present when at least one file
                exists so users can search/inspect even when only a handful
                are uploaded. Reads "View all files (N)" so the count is
                visible without opening the modal. */}
            {files.length > 0 && (
              <button
                type="button"
                onClick={() => setModalOpen(true)}
                className="self-start mt-2 text-sm font-semibold text-blue hover:text-primary hover:underline"
              >
                {hasMore
                  ? `View all files (${files.length})`
                  : 'View all files'}
              </button>
            )}
          </>
        )}
      </div>

      <UploadedFilesModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        files={files}
        loading={loading}
        canAddToZone={canAddToZone}
        onAddFile={(file, zoneType) => {
          onAddFile(file, zoneType);
          setModalOpen(false);
        }}
      />
    </div>
  );
}
